import React, { useCallback, useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Search, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

export default function AdminPatients() {
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const page = Number(sp.get("page") || 1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, headers } = await adminApi.get("/admin/patients", { params: { q: q || undefined, page, page_size: 20 } });
      setRows(data);
      setTotal(Number(headers["x-total-count"] || 0));
    } catch (e) { toast.error("Failed to load patients"); }
    finally { setLoading(false); }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);

  const setPage = (p) => setSp((prev) => { const n = new URLSearchParams(prev); if (p <= 1) n.delete("page"); else n.set("page", String(p)); return n; });

  const remove = async (p) => {
    if (!window.confirm(`Delete patient "${p.name}"? This cannot be undone.`)) return;
    setBusyId(p.id);
    try {
      await adminApi.delete(`/admin/patients/${p.id}`);
      toast.success("Patient deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusyId(null); }
  };

  const from = (page - 1) * 20 + 1;
  const to = (page - 1) * 20 + rows.length;

  return (
    <div className="max-w-7xl">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#8A9992]">Patient Management</div>
          <h1 className="text-3xl font-semibold text-[#1F2A24] mt-1" style={{ fontFamily: "Instrument Sans" }}>Patients {total > 0 && <span className="text-[#8A9992] font-normal">({total})</span>}</h1>
        </div>
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9992]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or mobile"
            className="input-clean pl-9"
            data-testid="admin-patients-search"
          />
        </div>
      </div>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-patients-table">
            <thead>
              <tr className="bg-[#F7F4EC] text-[#5C7568] text-[11px] uppercase tracking-widest">
                <Th>Patient</Th>
                <Th>Gender / Age</Th>
                <Th>Mobile</Th>
                <Th>Doctor</Th>
                <Th>Created</Th>
                <Th className="text-right pr-6">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-12 text-center text-[#8A9992]"><Loader2 className="animate-spin inline mr-2" size={14} />Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center text-[#8A9992]">No patients match.</td></tr>
              ) : (
                rows.map((p) => (
                  <tr key={p.id} className="border-t border-[#EFECE5] hover:bg-[#FDFBF7]" data-testid={`patient-row-${p.id}`}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#FCE7D9] text-[#5C3A21] font-semibold flex items-center justify-center text-xs">
                          {(p.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-[#1F2A24]">{p.name || "Unnamed"}</div>
                          <div className="text-[11px] text-[#8A9992]">{p.id?.slice(0, 8)}</div>
                        </div>
                      </div>
                    </Td>
                    <Td>{p.gender || "—"}{p.age_value ? ` · ${p.age_value} ${p.age_type || "YRS"}` : ""}</Td>
                    <Td>{p.mobile || <span className="text-[#B5C0BA]">—</span>}</Td>
                    <Td className="text-[13px] text-[#47554D]">
                      {p.doctor_name || "—"}
                      {p.doctor_email && <div className="text-[10px] text-[#8A9992]">{p.doctor_email}</div>}
                    </Td>
                    <Td className="text-[12px] text-[#6C7B72]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</Td>
                    <Td className="text-right pr-4">
                      <button
                        onClick={() => remove(p)}
                        disabled={busyId === p.id}
                        title="Delete patient"
                        data-testid={`delete-patient-${p.id}`}
                        className="w-8 h-8 rounded-full text-[#B14A2A] hover:bg-[#F8E7DF] disabled:opacity-40 inline-flex items-center justify-center"
                      >
                        {busyId === p.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-6 py-3 border-t border-[#EFECE5] text-xs text-[#6C7B72]">
          <div>{rows.length > 0 ? `${from}-${to} of ${total}` : "0 of 0"}</div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="pill-tab pill-tab-inactive disabled:opacity-40">Prev</button>
            <button disabled={page * 20 >= total} onClick={() => setPage(page + 1)} className="pill-tab pill-tab-inactive disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-4 py-3 font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}
